import { tx as translateInterfaceText } from '@/utils/i18n';
import { api } from '@/services/api';
import type {
  FlowGraph,
  FlowTheme,
  FlowValidation,
  ValidationIssue,
} from '@/lib/vsl-dinamica/contract';

// API admin da VSL Dinâmica v2 (e-video-vsl-be: modules/dynamic-flows).
// O BE responde { success, data } e os erros { success:false, code, message, details? }.

export type FlowStatus = 'draft' | 'published' | 'paused';

export interface FlowListItem {
  id: string;
  name: string;
  status: FlowStatus;
  public_id: string;
  published_version: number | null;
  has_unpublished_changes: boolean;
  video_count: number;
  updated_at: string;
  stats_30d: { views: number; plays: number };
}

export interface FlowLayout {
  positions: Record<string, { x: number; y: number }>;
  viewport: { x: number; y: number; zoom: number } | null;
}

export interface FlowDetail {
  id: string;
  name: string;
  status: FlowStatus;
  public_id: string;
  published_version: number | null;
  published_at: string | null;
  has_unpublished_changes: boolean;
  draft: { revision: number; graph: FlowGraph; theme: FlowTheme; updated_at: string | null };
  editor_layout: FlowLayout;
  validation: FlowValidation;
}

export interface FlowVideo {
  id: string;
  name: string;
  frame: string | null;
  hls_url: string | null;
  duration: number | null;
  width: number | null;
  height: number | null;
  processing_status: string;
  ready: boolean;
}

export interface SaveDraftResult {
  revision: number;
  validation: FlowValidation;
  has_unpublished_changes: boolean;
}

export interface PublishResult {
  version: number;
  published_at: string;
  status: FlowStatus;
  has_unpublished_changes: boolean;
  changed: boolean;
  warnings: ValidationIssue[];
}

export interface PreviewResult {
  manifest: Record<string, unknown>;
  validation: FlowValidation;
}

export interface MetricsStep {
  node_id: string;
  label: string;
  is_start: boolean;
  entries: number;
  played: number;
  play_rate: number;
  avg_watched: number;
}

export interface MetricsChoice {
  node_id: string;
  node_label: string;
  choice_id: string;
  letter: string;
  label: string;
  target_label: string;
  target_type: 'node' | 'exit';
  clicks: number;
  shown: number;
  rate: number;
}

export interface MetricsExit {
  exit_id: string;
  label: string;
  is_checkout: boolean;
  clicks: number;
  approved_sales?: number | null;
  revenue?: number | null;
}

// Um ponto da curva de retenção (mesmo cálculo da plataforma: a cada 5 s + último segundo).
export interface RetentionPoint {
  time: string;
  seconds: number;
  percent: number;
}

export interface MetricsVideo {
  video_id: string;
  name: string;
  frame: string | null;
  duration: number | null;
  views: number;
  unique_views: number;
  plays: number;
  unique_plays: number;
  play_rate: number;
  avg_watched: number;
  avg_watched_seconds: number;
  retention: RetentionPoint[];
}

export interface FlowSales {
  connected: boolean;
  approved_sales: number | null;
  revenue: number | null;
  conversion_rate: number | null;
}
export interface FlowTrackingConnection { exit_id: string; platform_id: string; webhook_url: string; active: boolean; }
export interface FlowTracking { platforms: { id: string; name: string }[]; connections: FlowTrackingConnection[]; }
export interface FlowMetrics {
  sales?: FlowSales;
  range: { from: string; to: string };
  totals: {
    views: number;
    plays: number;
    play_rate: number;
    checkout_sessions: number;
    conversion_proxy: number;
  };
  steps: MetricsStep[];
  choices: MetricsChoice[];
  exits: MetricsExit[];
  videos?: MetricsVideo[];
}

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get issues(): ValidationIssue[] {
    const details = this.details as { errors?: ValidationIssue[] } | undefined;
    return details?.errors || [];
  }
}

export class DraftConflictError extends ApiError {
  revision: number;

  constructor(message: string, revision: number) {
    super(409, 'DRAFT_CONFLICT', message, { revision });
    this.revision = revision;
  }
}

const FRIENDLY: Record<string, string> = {
  get TRIAL_LIMIT() { return translateInterfaceText("No período de teste você pode enviar até 3 vídeos para a VSL Dinâmica."); },
  get ACCOUNT_INACTIVE() { return translateInterfaceText("Sua conta não está ativa para enviar vídeos."); },
  VIDEO_IN_USE: 'Este vídeo está em uso numa etapa. Troque o vídeo da etapa antes de excluir.',
};

interface ApiErrorBody {
  code?: string;
  message?: string;
  details?: { revision?: number; errors?: ValidationIssue[] };
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  const response = (error as { response?: { status?: number; data?: ApiErrorBody } })?.response;
  const status = response?.status ?? 0;
  const data: ApiErrorBody = response?.data || {};
  const code: string = data.code || (status ? `HTTP_${status}` : 'NETWORK_ERROR');
  const message: string =
    FRIENDLY[code] ||
    data.message ||
    (status ? translateInterfaceText("Não foi possível concluir. Tente novamente.") : translateInterfaceText("Sem conexão com o servidor."));
  if (status === 409 && code === 'DRAFT_CONFLICT') {
    return new DraftConflictError(message, Number(data.details?.revision ?? 0));
  }
  return new ApiError(status, code, message, data.details);
}

async function call<T>(request: () => Promise<{ data: T }>): Promise<T> {
  try {
    const body = await request();
    return body.data;
  } catch (error) {
    throw toApiError(error);
  }
}

function readVideoMetadata(file: File): Promise<{ width: number; height: number; duration: number }> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    const done = (meta: { width: number; height: number; duration: number }) => {
      URL.revokeObjectURL(url);
      resolve(meta);
    };
    video.preload = 'metadata';
    video.onloadedmetadata = () =>
      done({
        width: video.videoWidth || 0,
        height: video.videoHeight || 0,
        duration: Number.isFinite(video.duration) ? video.duration : 0,
      });
    // Formato que o navegador não lê (ex.: alguns .mov): o processamento descobre depois.
    video.onerror = () => done({ width: 0, height: 0, duration: 0 });
    video.src = url;
  });
}

// VITE_API já termina em /api (como em /videos/upload-video-v2).
const BASE = '/dynamic-flows';

export const dynamicFlowsService = {
  list: () => call<FlowListItem[]>(() => api.get(BASE)),

  create: (name: string) => call<FlowDetail>(() => api.post(BASE, { name })),

  get: (id: string) => call<FlowDetail>(() => api.get(`${BASE}/${id}`)),

  rename: (id: string, name: string) =>
    call<{ id: string; name: string }>(() => api.patch(`${BASE}/${id}`, { name })),

  saveDraft: (id: string, body: { base_revision: number; graph: FlowGraph; theme: FlowTheme }) =>
    call<SaveDraftResult>(() => api.put(`${BASE}/${id}/draft`, body)),

  saveLayout: (id: string, layout: FlowLayout) =>
    call<unknown>(() => api.put(`${BASE}/${id}/layout`, layout)),

  previewManifest: (id: string) =>
    call<PreviewResult>(() => api.post(`${BASE}/${id}/preview-manifest`)),

  publish: (id: string) => call<PublishResult>(() => api.post(`${BASE}/${id}/publish`)),

  pause: (id: string) => call<{ status: FlowStatus }>(() => api.post(`${BASE}/${id}/pause`)),

  resume: (id: string) => call<{ status: FlowStatus }>(() => api.post(`${BASE}/${id}/resume`)),

  remove: (id: string) =>
    call<{ deleted: boolean; videos_deleted: number }>(() => api.delete(`${BASE}/${id}`)),

  listVideos: (id: string) => call<FlowVideo[]>(() => api.get(`${BASE}/${id}/videos`)),

  async uploadVideo(id: string, file: File, onProgress?: (percent: number) => void): Promise<unknown> {
    const meta = await readVideoMetadata(file);
    const form = new FormData();
    form.append('video', file);
    form.append('inputedname', file.name.replace(/\.[^/.]+$/, ''));
    form.append('VideoDuration', String(meta.duration));
    form.append('videoWidth', String(meta.width));
    form.append('videoHeight', String(meta.height));
    try {
      return await api.post(`${BASE}/${id}/videos?dynamic_vsl=true`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30 * 60 * 1000,
        onUploadProgress: (event: { loaded: number; total?: number }) => {
          if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
        },
      });
    } catch (error) {
      throw toApiError(error);
    }
  },

  renameVideo: (id: string, videoId: string, name: string) =>
    call<unknown>(() => api.patch(`${BASE}/${id}/videos/${videoId}`, { name })),

  deleteVideo: (id: string, videoId: string) =>
    call<unknown>(() => api.delete(`${BASE}/${id}/videos/${videoId}`)),

  tracking: (id: string) => call<FlowTracking>(() => api.get(`${BASE}/${id}/tracking`)),
  connectTracking: (id: string, body: { exit_id: string; platform_id: string; platform_token?: string }) =>
    call<FlowTrackingConnection>(() => api.post(`${BASE}/${id}/tracking`, body)),

  metrics: (id: string, range: { from: string; to: string }) =>
    call<FlowMetrics>(() => api.get(`${BASE}/${id}/metrics`, { params: range })),
};
